import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule, RouterOutlet } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, takeUntil } from 'rxjs/operators';
import { APP_SETTINGS, AppSettings } from 'src/app.settings';
import { ApplicationService as AppApplicationService } from 'src/app/services/craftlink/application.service';
import { AuthService, ExtendedIdTokenClaims } from './services/auth.service';
import { LocaleService, SUPPORTED_LOCALE } from './services/locale.service';
import { LoggingService } from './services/logging.service';
import { AccountInformationResponse, AccountService } from './services/openapi';
import { RoleStateService } from './services/role-state.service';
import { FooterComponent } from './shared/components/footer/footer.component';
import { LoadingIndicatorComponent } from './shared/components/loading-indicator/loading-indicator.component';
import { LocaleSelectorComponent } from './shared/components/locale-selector/locale-selector.component';
import {
  CountrySwitchDialogComponent,
  CountrySwitchDialogData,
} from './shared/dialogs/country-switch-dialog/country-switch-dialog.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    AsyncPipe,
    NgClass,
    RouterModule,
    RouterOutlet,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    FooterComponent,
    LoadingIndicatorComponent,
    LocaleSelectorComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly appSettings: AppSettings = inject(APP_SETTINGS);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly msalService: MsalService = inject(MsalService);
  private readonly msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly authService: AuthService = inject(AuthService);
  private readonly localeService: LocaleService = inject(LocaleService);
  private readonly loggingService: LoggingService = inject(LoggingService);
  private readonly accountService: AccountService = inject(AccountService);
  private readonly roleStateService: RoleStateService = inject(RoleStateService);
  private readonly appApplicationService: AppApplicationService = inject(AppApplicationService);
  private readonly _destroying$ = new Subject<void>();
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );
  isIframe = false;
  loginDisplay = signal(false);
  portalDisplay = signal(false);
  evaluatorDisplay = signal(false);

  constructor() {
    this.loggingService.init();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initAuth();
      this.initLocale();
    }
  }

  private initAuth(): void {
    this.msalService.handleRedirectObservable().subscribe();

    this.isIframe = window !== window.parent && !window.opener;

    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.setPortalDisplay();
        this.setEvaluatorDisplay();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as ExtendedIdTokenClaims;

        if (idtoken.idp) {
          this.authService.IsLocalIdp = false;
        }

        this.authService.handleSignUpSignInPolicy(payload, idtoken);
        this.authService.handleEditProfilePolicy(idtoken);
        this.authService.handleChangePasswordPolicy(idtoken);
        this.authService.handleChangeSignInNamePolicy(idtoken);
        this.authService.handleDeleteAccountPolicy(idtoken);
        this.authService.handleSocialAccountLinkingPolicy(idtoken);

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as ExtendedIdTokenClaims;

        if (
          idtoken.acr?.toLowerCase() === this.appSettings.B2C.SignUpSignInPolicy.toLowerCase() ||
          idtoken.tfp?.toLowerCase() === this.appSettings.B2C.SignUpSignInPolicy.toLowerCase()
        ) {
          this.authService.handleAccountRetrieval(idtoken);
          this.authService.updateLastLoggedInDate(idtoken);
        }
      });
  }

  private initLocale(): void {
    this.localeService.shouldPromptUserCountrySwitch().subscribe((geoLocationResponse) => {
      if (geoLocationResponse === null) {
        return;
      }
      const locale = `${this.localeService.languageCode}-${geoLocationResponse}` as SUPPORTED_LOCALE;
      const dialogData: CountrySwitchDialogData = {
        recommendedLocale: locale,
      };
      this.dialog.open(CountrySwitchDialogComponent, { data: dialogData, autoFocus: false });
    });
  }

  private setLoginDisplay(): void {
    this.loginDisplay.set(this.msalService.instance.getAllAccounts().length > 0);
    if (!this.loginDisplay()) {
      this.portalDisplay.set(false);
      this.evaluatorDisplay.set(false);
      this.roleStateService.clearAll();
    }
  }

  private setPortalDisplay(): void {
    if (!this.loginDisplay()) {
      return;
    }

    this.accountService.getAccount().subscribe((result: AccountInformationResponse) => {
      if (this.localeService.isCA) {
        this.portalDisplay.set(result.caHasPortalAccess);
      } else {
        this.portalDisplay.set(result.usHasPortalAccess);
      }
    });
  }

  private setEvaluatorDisplay(): void {
    if (!this.loginDisplay()) {
      return;
    }

    this.roleStateService.ensureRolesLoaded().subscribe({
      next: (result) => {
        this.evaluatorDisplay.set(!!result.isEvaluator);
      },
      error: () => {
        this.evaluatorDisplay.set(false);
      },
    });
  }

  private checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  logout() {
    if (this.appApplicationService.activeJobPosting) {
      this.appApplicationService.clearActiveJobPosting();
    }

    this.roleStateService.clearAll();

    this.msalService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
